
import { defineComponent } from 'vue'
import ActiveUserMixin from '~/mixins/activeUserMixin'
import AuthMixin from '~/mixins/authMixin'
import { FileUploadContexts, FileUploadTokenT, uploadFiles } from '~/utils/files'
import { getDefaultAvatar } from '~/utils/ui'

export default defineComponent({
  name: 'UserProfile',
  mixins: [ActiveUserMixin, AuthMixin],
  data() {
    return {
      editMode: false,
      displayName: '',
      isSaving: false,
      newAvatar: {
        filename: '',
        token: '',
        url: ''
      } as FileUploadTokenT
    }
  },
  async fetch() {
    await this.fetchActiveUser({ refetch: true })
  },
  methods: {
    getDefaultAvatar,
    /**
     * Toggles the component between 'edit' and 'view' modes
     *
     * @returns {void}
     */
    toggleEditMode() {
      this.editMode = !this.editMode
      if (this.editMode) {
        this.displayName = this.viewer.fullName || this.viewer.firstName
      } else this.reset()
    },
    /**
     * Handles the file upload button click event
     *
     * @returns {void}
     */
    onFileUploadClick() {
      const fileInput = this.$refs.fileInput as HTMLInputElement
      if (fileInput) fileInput.click()
    },
    /**
     * Handles the change event of the file input element. Triggers a file upload if files have been selected
     *
     * @param {Array<File>} fileList
     */
    filesChange(event: Event) {
      if (!event.target) return
      const fileList = (event.target as HTMLInputElement).files
      if (!fileList?.length) return
      this.uploadAvatar(fileList[0])
    },
    /**
     * Validates the inputs for email and display name
     *
     * @returns {boolean | void}
     */
    validateInputs() {
      if (!this.displayName) {
        throw new Error('Display name is required')
      }
      if (this.displayName.length < 3) {
        throw new Error('Display name must be 3 characters or longer')
      }
      return true
    },

    /**
     * Resets the component state in case changes are discarded
     *
     * @returns {void}
     */
    reset() {
      this.editMode = false
      this.displayName = ''
      this.newAvatar = {
        filename: '',
        token: '',
        url: ''
      } as FileUploadTokenT
    },
    /**
     * Handles the upload of the selected display picture, and updates `newAvatar` with the URL received from the API call
     *
     * @param {File} file
     */
    async uploadAvatar(file: File) {
      try {
        const fileUploadTokens = await uploadFiles(
          this.$config.restClientUri,
          FileUploadContexts.avatars,
          [file],
          {
            headers: {
              Authorization: `JWT ${this.token}`
            }
          }
        )
        this.newAvatar = fileUploadTokens[0]
      } catch (err) {
        this.$toast.show({
          type: 'danger',
          message:
            'An Error occurred while uploading the new Avatar. Please try again or contact support.',
          timeout: 5
        })

        this.$logErrorAndToast(err as Error)
      }
    },
    /**
     * Saves the updated user profile details (display name, email, avatar).
     * Refreshes the JWT since the existing one has the old email
     * Re-fetches the viewer
     *
     * @returns {void}
     */
    async saveDetails() {
      try {
        this.isSaving = true

        if (this.validateInputs()) {
          const updatedViewer = await this.updateUserDetails({
            displayName: this.displayName,
            avatarToken: this.newAvatar.token || undefined
          })
          if (updatedViewer) {
            await this.fetchActiveUser({ refetch: true })
            this.reset()
            this.$toast.success('User details updated.')
          }
        }
      } catch (err) {
        const error = err as Error
        const errorMessage = `${error.message}.` as `${string}.`
        this.$logErrorAndToast(error, errorMessage)
      } finally {
        this.isSaving = false
      }
    }
  }
})
